import type { RastroPendenciaEntry } from '@/features/secretaria-arbitral/types';

/** Duas escritas do mesmo ato nunca distam mais que o tempo da transação. */
const JANELA_MESMA_TRANSICAO_MS = 5000;

const chaveDaTransicao = (entrada: RastroPendenciaEntry): string =>
  `${entrada.statusAnterior ?? ''}>${entrada.statusNovo ?? ''}|${entrada.autor ?? ''}`;

/** Linha que só reescreve o status: é o que o trigger produz, e nunca o que o serviço grava. */
const semConteudoProprio = (entrada: RastroPendenciaEntry): boolean =>
  !entrada.andamentoId && !entrada.descricao;

/**
 * Colapsa a dupla de histórico que **toda** transição de pendência gera: uma linha escrita
 * pelo serviço e outra pelo trigger `trg_hist_pendencia_arbitral_status` (de março/2026, no
 * banco). Sem isto o operador lê "Aberta → Resolvida" duas vezes para um único ato.
 *
 * Fica no front porque a duplicação é do banco e some quando o trigger sair — dedupe aqui é
 * reversível, mudar o trigger não é.
 *
 * O par só é colapsado quando pelo menos uma das linhas é **muda** — sem `andamentoId` e sem
 * `descricao`. Essa é a assinatura do trigger, que só reescreve o status. A regra não é
 * cosmética: `ABERTA → ABERTA` é transição LEGÍTIMA e repetível (é o bate-bola do
 * `MANTER_ABERTA`) e dois bate-bolas seguidos trazem observações diferentes — colapsar por
 * "mesmo par de status" apagaria o texto do segundo. Duas linhas que falam ficam ambas.
 */
export function deduplicarRastro(entradas: RastroPendenciaEntry[]): RastroPendenciaEntry[] {
  const resultado: RastroPendenciaEntry[] = [];

  for (const entrada of entradas) {
    if (entrada.tipo !== 'STATUS') {
      resultado.push(entrada);
      continue;
    }

    const instante = Date.parse(entrada.dataEvento);
    const gemeaIndex = resultado.findIndex(
      (anterior) =>
        anterior.tipo === 'STATUS' &&
        chaveDaTransicao(anterior) === chaveDaTransicao(entrada) &&
        (semConteudoProprio(anterior) || semConteudoProprio(entrada)) &&
        Math.abs(Date.parse(anterior.dataEvento) - instante) <= JANELA_MESMA_TRANSICAO_MS,
    );

    if (gemeaIndex === -1) {
      resultado.push(entrada);
      continue;
    }

    if (semConteudoProprio(resultado[gemeaIndex])) {
      resultado[gemeaIndex] = entrada;
    }
  }

  return resultado;
}
